import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';
import {
  EquipmentConfigItem,
  EquipmentConfigRequest,
  EquipmentListResponse,
  EquipmentMutationResponse,
  PhotoCategoryItem,
  PhotoCategoryRequest,
  PhotoCategoryListResponse,
  PhotoCategoryMutationResponse,
  RouteTypeItem,
  RouteTypeRequest,
  RouteTypeListResponse,
  RouteTypeMutationResponse,
  LoadSequenceItem,
  LoadSequenceRequest,
  LoadSequenceListResponse,
  LoadSequenceMutationResponse,
} from './types';

const getClientTimeParams = () => ({
  clientTimestamp: getLocalTimestamp(),
  clientTimezone: getTimezoneOffsetString(),
});

// ─── Last Updated ─────────────────────────────────────────────────────────────

export interface LastUpdatedResponse {
  updatedBy?: string | null;
  updatedAt?: string | null;
  displayText: string;
}

export const fetchLastUpdated = async (): Promise<LastUpdatedResponse> => {
  const api = createApiClient();
  const res = await api.get<LastUpdatedResponse>('/perfect-truck/config/last-updated');
  return res.data;
};

// ─── Equipment ────────────────────────────────────────────────────────────────

export const fetchEquipment = async (language?: string): Promise<EquipmentConfigItem[]> => {
  const api = createApiClient();
  const params = language ? { language } : undefined;
  const res = await api.get<EquipmentListResponse>('/perfect-truck/config/equipment', { params });
  return res.data.data;
};

export const checkEquipmentExists = async (equipmentName: string): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ excludeId: string; exists: boolean; equipmentName: string }>(
    '/perfect-truck/config/equipment/check-exists',
    { params: { equipmentName } },
  );
  return res.data.exists;
};

export const checkEquipmentDisplayOrderExists = async (displayOrder: number): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ excludeId: string; exists: boolean; displayOrder: number }>(
    '/perfect-truck/config/equipment/check-display-order-exists',
    { params: { displayOrder } },
  );
  return res.data.exists;
};

export const createEquipment = async (
  data: EquipmentConfigRequest[] | any[],
): Promise<EquipmentConfigItem> => {
  const api = createApiClient();
  const res = await api.post<EquipmentMutationResponse>('/perfect-truck/config/equipment', data, { params: getClientTimeParams() });
  return res.data.data;
};

export const updateEquipment = async (
  id: number,
  data: EquipmentConfigRequest[] | any[],
): Promise<EquipmentConfigItem> => {
  const api = createApiClient();
  const res = await api.put<EquipmentMutationResponse>(`/perfect-truck/config/equipment/${id}`, data, { params: getClientTimeParams() });
  return res.data.data;
};

export const toggleEquipmentActive = async (
  id: number,
  isActive: boolean,
  requestedBy: string,
): Promise<EquipmentConfigItem> => {
  const api = createApiClient();
  const res = await api.patch<EquipmentMutationResponse>(
    `/perfect-truck/config/equipment/${id}/toggle-active`,
    { isActive, requestedBy },
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

// ─── Photo Categories ─────────────────────────────────────────────────────────

export const fetchPhotoCategories = async (language?: string): Promise<PhotoCategoryItem[]> => {
  const api = createApiClient();
  const params = language ? { language } : undefined;
  const res = await api.get<PhotoCategoryListResponse>('/perfect-truck/config/photo-categories', { params });
  return res.data.data;
};

export const checkPhotoCategoryExists = async (photoLabel: string): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ excludeId: string; exists: boolean; photoLabel: string }>(
    '/perfect-truck/config/photo-categories/check-exists',
    { params: { photoLabel } },
  );
  return res.data.exists;
};

export const checkPhotoDisplayOrderExists = async (displayOrder: number): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ excludeId: string; exists: boolean; displayOrder: number }>(
    '/perfect-truck/config/photo-categories/check-display-order-exists',
    { params: { displayOrder } },
  );
  return res.data.exists;
};

export const createPhotoCategory = async (
  data: PhotoCategoryRequest | PhotoCategoryRequest[],
): Promise<PhotoCategoryItem> => {
  const api = createApiClient();
  const res = await api.post<PhotoCategoryMutationResponse>('/perfect-truck/config/photo-categories', data, { params: getClientTimeParams() });
  return res.data.data;
};

export const updatePhotoCategory = async (
  id: number,
  data: PhotoCategoryRequest | PhotoCategoryRequest[],
): Promise<PhotoCategoryItem> => {
  const api = createApiClient();
  const res = await api.put<PhotoCategoryMutationResponse>(`/perfect-truck/config/photo-categories/${id}`, data, { params: getClientTimeParams() });
  return res.data.data;
};

export const togglePhotoCategoryActive = async (
  id: number,
  isActive: boolean,
  requestedBy: string,
): Promise<PhotoCategoryItem> => {
  const api = createApiClient();
  const res = await api.patch<PhotoCategoryMutationResponse>(
    `/perfect-truck/config/photo-categories/${id}/toggle-active`,
    { isActive, requestedBy },
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

// ─── Route Types ──────────────────────────────────────────────────────────────

export const fetchRouteTypes = async (arg?: boolean | string): Promise<RouteTypeItem[]> => {
  const api = createApiClient();
  let params: any = undefined;
  if (typeof arg === 'boolean') params = { isActive: arg };
  else if (typeof arg === 'string') params = { language: arg };
  const res = await api.get<RouteTypeListResponse>('/perfect-truck/config/route-types', { params });
  return res.data.data;
};

export const checkRouteTypeExists = async (routeType: string): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ excludeId: string; routeType: string; exists: boolean }>(
    '/perfect-truck/config/route-types/check-exists',
    { params: { routeType } },
  );
  return res.data.exists;
};

export const createRouteType = async (
  data: RouteTypeRequest | RouteTypeRequest[],
): Promise<RouteTypeItem> => {
  const api = createApiClient();
  const res = await api.post<RouteTypeMutationResponse>('/perfect-truck/config/route-types', data, { params: getClientTimeParams() });
  return res.data.data;
};

export const updateRouteType = async (
  id: number,
  data: RouteTypeRequest | RouteTypeRequest[],
): Promise<RouteTypeItem> => {
  const api = createApiClient();
  const res = await api.put<RouteTypeMutationResponse>(`/perfect-truck/config/route-types/${id}`, data, { params: getClientTimeParams() });
  return res.data.data;
};

export const toggleRouteTypeActive = async (
  id: number,
  isActive: boolean,
  requestedBy: string,
): Promise<RouteTypeItem> => {
  const api = createApiClient();
  const res = await api.patch<RouteTypeMutationResponse>(
    `/perfect-truck/config/route-types/${id}/toggle-active`,
    { isActive, requestedBy },
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

// ─── Load Sequences ───────────────────────────────────────────────────────────

export const fetchLoadSequences = async (arg?: boolean | string): Promise<LoadSequenceItem[]> => {
  const api = createApiClient();
  let params: any = undefined;
  if (typeof arg === 'boolean') params = { isActive: arg };
  else if (typeof arg === 'string') params = { language: arg };
  const res = await api.get<LoadSequenceListResponse>('/perfect-truck/config/load-sequences', { params });
  return res.data.data;
};

export const checkLoadSequenceExists = async (loadSequence: string): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ exists: boolean; loadSequence: string }>(
    '/perfect-truck/config/load-sequences/check-exists',
    { params: { loadSequence } },
  );
  return res.data.exists;
};

export const checkLoadSequenceDisplayOrderExists = async (
  displayOrder: number,
  excludeId?: number,
): Promise<boolean> => {
  const api = createApiClient();
  const res = await api.get<{ exists: boolean; displayOrder: number }>(
    '/perfect-truck/config/load-sequences/check-display-order-exists',
    { params: { displayOrder, ...(excludeId !== undefined ? { excludeId } : {}) } },
  );
  return res.data.exists;
};

export const createLoadSequence = async (
  data: LoadSequenceRequest[] | any[],
): Promise<LoadSequenceItem> => {
  const api = createApiClient();
  const res = await api.post<LoadSequenceMutationResponse>(
    '/perfect-truck/config/load-sequences',
    data,
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

export const updateLoadSequence = async (
  id: number,
  data: LoadSequenceRequest[] | any[],
): Promise<LoadSequenceItem> => {
  const api = createApiClient();
  const res = await api.put<LoadSequenceMutationResponse>(
    `/perfect-truck/config/load-sequences/${id}`,
    data,
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

export const toggleLoadSequenceActive = async (
  id: number,
  isActive: boolean,
  requestedBy: string,
  item: Pick<LoadSequenceItem, 'loadSequence' | 'displayOrder' | 'locationType' | 'organizationType' | 'is3pl'>,
): Promise<LoadSequenceItem> => {
  const api = createApiClient();
  const res = await api.put<LoadSequenceMutationResponse>(
    `/perfect-truck/config/load-sequences/${id}`,
    {
      loadSequence: item.loadSequence,
      displayOrder: item.displayOrder,
      locationType: item.locationType ?? null,
      organizationType: item.organizationType ?? null,
      is3pl: item.is3pl,
      isActive,
      requestedBy,
    } satisfies LoadSequenceRequest,
    { params: getClientTimeParams() },
  );
  return res.data.data;
};
